import { VOOrchestra } from "../model/DataStructure.js";
import { Labels } from "../model/Labels.js";
import { Decoration } from "./Decoration.js";
import { OrchestraDetail } from "./OrchestraDetail.js";
import { Polygon, PolygonShape } from "./Polygon.js";

export interface OchestraSelectorOptions {
    container?: HTMLDivElement;
    orchestras: VOOrchestra[];
    visible?: boolean;
    labels: Labels;
}

export interface OrchestraSelectorEvent {
    orchestra: VOOrchestra;
}

export class OrchestraSelector {
    public readonly element: HTMLElement;
    public readonly onSelect = e5.Signal.create<[OrchestraSelectorEvent]>();

    private _options: OchestraSelectorOptions;
    private _decoration: Decoration;
    private _visible: boolean = true;
    private _detail: OrchestraDetail;
    private _orchestra: VOOrchestra = null;
    private _items: OrchestraSelectorItem[] = [];
    private _isAnimating = false;
    private _isIdle: boolean = true;

    constructor(options: OchestraSelectorOptions) {
        options = e5.object.merge({
            container: null,
            visible: true
        } as OchestraSelectorOptions, options);
        this._options = options;

        this.element = document.createElement("div");
        this.element.className = "orchestra-selector";
        this.element.classList.toggle("orchestra-selector--idle", this._isIdle);
        this.element.innerHTML = `
        <div class="orchestra-selector__decorations"></div>
        <div class="orchestra-selector__detail"></div>
        <div class="orchestra-selector__item-list"></div>
        <div class="orchestra-selector__title">${options.labels.html("orchestra.orchestra_selector.title")}</div>
        `;

        this._visible = options.visible;
        this.element.classList.toggle("orchestra-selector--hidden", !this._visible);

        this._decoration = new Decoration({
            container: this.element.querySelector(".orchestra-selector__decorations"),
            visible: options.visible,
            names: [
                "orchestra-selector-1",
                "orchestra-selector-2",
                "orchestra-selector-6",
                "orchestra-selector-14"
            ]
        });

        this._detail = new OrchestraDetail({
            container: this.element.querySelector(".orchestra-selector__detail"),
            labels: options.labels,
            onSelect: (orchestra) => {
                this.onSelect.dispatch({
                    orchestra: orchestra
                })
            },
            onVideoEnd: () => {
                if (!this._isIdle) {
                    return;
                }
                this.selectByOffset(1);
            }
        });

        let index = 0;
        for (let orchestra of options.orchestras) {
            const item = new OrchestraSelectorItem({
                data: orchestra,
                container: this.element.querySelector(".orchestra-selector__item-list"),
                index: index++,
                onClick: (orchestra) => this.setOrchestra(orchestra)
            });
            this._items.push(item);
        }

        this.setOrchestra(options.orchestras[0]);

        if (options.container) {
            options.container.appendChild(this.element);
        }
    }

    private selectByOffset(offset: number): void {
        const orchestras = this._options.orchestras;
        let index = orchestras.indexOf(this._orchestra);
        if (index === -1) {
            index = 0;
        }
        index = e5.Calc.wrap(index + offset, orchestras.length);
        this.setOrchestra(orchestras[index]);
    }

    public get isIdle(): boolean {
        return this._isIdle;
    }

    public set isIdle(value: boolean) {
        if (this._isIdle === value) {
            return;
        }
        this._isIdle = value;
        this.element.classList.toggle("orchestra-selector--idle", this._isIdle);
    }

    private getOrchestra(): VOOrchestra {
        return this._orchestra;
    }

    private async setOrchestra(value: VOOrchestra) {
        if (!value) {
            console.warn("Could not set empty orchestra.");
            return;
        }

        if (this._isAnimating) {
            console.warn("Can not open orchestra detail during animation.");
            return;
        }

        if (this._orchestra === value) {
            return;
        }

        this._isAnimating = true;

        await this._detail.hide();

        if (this._orchestra) {
            const item = this._items.find((v) => v.orchestra.id === this._orchestra.id);
            if (item) {
                item.selected = false;
            }
        }

        this._orchestra = value;
        this._detail.orchestra = value;

        if (this._orchestra) {
            const item = this._items.find((v) => v.orchestra.id === value.id);
            if (item) {
                item.selected = true;
            }
        }

        await this._detail.show();

        this._isAnimating = false;
    }

    public async show() {
        if (this._visible) {
            return false;
        }
        this._visible = true;

        this.element.classList.remove("e5-hidden");
        this.element.offsetWidth;

        this.element.classList.toggle("orchestra-selector--hidden", false);
        this._detail.active = true;

        this._decoration.visible = true;

        return new Promise<any>((resolve, reject) => {
            setTimeout(() => {
                resolve(true);
            }, 500);
        });
    }

    public async hide() {
        if (!this._visible) {
            return false;
        }
        this._visible = false;
        this.element.classList.toggle("orchestra-selector--hidden", true);

        this._decoration.visible = false;

        this._detail.active = false;

        return new Promise<any>((resolve, reject) => {
            setTimeout(() => {
                if (!this._visible) {
                    this.element.classList.add("e5-hidden");
                }
                resolve(true);
            }, 500);
        });
    }
}

interface OrchestraSelectorItemOptions {
    data: VOOrchestra;
    container: HTMLElement;
    index: number;
    onClick?: (orchestra: VOOrchestra) => void;
}

class OrchestraSelectorItem {
    public readonly element: HTMLButtonElement;
    public readonly orchestra: VOOrchestra;
    public onClick = e5.Signal.create<[VOOrchestra]>();

    private _polygon: Polygon;

    constructor(options: OrchestraSelectorItemOptions) {
        this.orchestra = options.data;

        this.element = document.createElement("button");
        this.element.className = "orchestra-selector__item";
        this.element.innerHTML = `
        <div class="orchestra-selector__item-name">
            ${e5.Text.escapeHtml(options.data.name)}
        </div>
        `;

        const shapeVariations: PolygonShape[] = ["1", "3", "4", "4"];

        this._polygon = new Polygon({
            container: this.element,
            color: options.data.color,
            shadowColor: options.data.color,
            shadowDistance: 24,
            prepend: true,
            background: true,
            shape: shapeVariations[options.index % shapeVariations.length],
            shadow: true
        });
        this._polygon.background.style.backgroundImage = `url(${options.data.thumbnail.url})`;

        if (typeof options.onClick === "function") {
            this.onClick.add(options.onClick);
        }

        this.element.addEventListener("click", () => {
            this.onClick.dispatch(this.orchestra);
        });

        options.container.appendChild(this.element);
    }

    public get selected(): boolean {
        return this.element.classList.contains("orchestra-selector__item--selected");
    }

    public set selected(value: boolean) {
        this.element.classList.toggle("orchestra-selector__item--selected", value);
        this.element.disabled = value;
    }
}

